<template>
    <PageSection>
        <PageHeader title="Reports" />
        <div class="card border border-purple-100 p-4 w-5/12 flex-grow-0 min-h-9/12 justify-start" id="report-card">
            <p class="pb-4"><span class="font-semibold">
                    Select a report to get started
                    <br /></span>
                    Please select a date range to download a report
                 </p>
            <form novalidate>
                <div class="gap-4">
                    <SelectMenu :items="avilableReports" name="version" label="Report"
                        default-text="Select type of report" class="w-6/12" />
                    <template v-if="hasChosenReport">
                        <SelectMenu :items="counterType" name="report" label="COUNTER Type" default-test="Select type"
                            class="w-6/12" :required="true" />
                        <div class="flex flex-row gap-4">
                            <DatePicker id="startDate" title="Select Month" :error="false" class="flex-grow"
                                teleport="#report-card" label="From" :required="true" :monthPicker="true"
                                :max-date="maxStartDate" :min-date="firstMonth" />
                            <DatePicker id="endDate" title="Select Month" :error="false" class="flex-grow"
                                teleport="#report-card" label="To" :required="true" :monthPicker="true"
                                :max-date="lastMonth" :min-date="firstMonth" />
                        </div>
                        <Button :loading="isDownloading" @click="download">Download</Button>
                    </template>
                </div>
            </form>
        </div>
    </PageSection>
</template>

<script lang="ts" setup>
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { definePage } from "vue-router/auto";
import DatePicker from "../../../../shared/components/Form/Date.vue";
import SelectMenu from "../../../../shared/components/Form/SelectMenu.vue";
import PageSection from "../../../../shared/components/PageSection.vue";
import { useInstitution as useInstitutionConfig } from "../../composables/useInstitution";
import { useReport } from "../../composables/useReportActor";
import { CounterReport, counterReport } from "../../schemas/counter-report";

type MonthPickerDate = {
	year: number;
	month: number;
};

const { institutionRegion, institutionId } = useInstitutionConfig();

const { isDownloading, downloadReport } = useReport();

const { values, handleSubmit } = useForm<CounterReport>({
	validationSchema: toTypedSchema(counterReport),
	initialValues: {
		version: "v5.1",
	},
});

const download = handleSubmit((formValues) => {
	if (isDownloading.value) return;
	downloadReport(formValues);
});

const reportSelections: {
	id: CounterReport["version"];
	label: string;
}[] = [
	{
		id: "v5.1",
		label: "COUNTER 5.1",
	},
];

const avilableReports = computed(() => {
	return reportSelections.filter((report) => {
		if (
			report.label === "COUNTER 5.1" &&
			institutionRegion.value.toLowerCase() !== "gb" &&
			institutionId.value.toLowerCase() !== "malaysiamoh" &&
			institutionId.value.toLowerCase() !== "bibliotechdemoinstitution" &&
			institutionId.value.toLowerCase() !== "bibliu"
		) {
			return false;
		}
		return true;
	});
});

const lastMonth = computed(() => {
	const now = new Date();
	return new Date(now.getFullYear(), now.getMonth(), 0);
});

const firstMonth = computed(() => {
	const now = new Date();
	return new Date(now.getFullYear() - 2, now.getMonth() - 1, 1);
});

const maxStartDate = computed(() => {
	if (values.endDate > lastMonth.value) {
		return lastMonth.value;
	}
	return (
		parseMonthPickerDateToDate(values.endDate as unknown as MonthPickerDate) ||
		lastMonth.value
	);
});

const counterType: {
	id: CounterReport["report"];
	label: string;
}[] = [
	{
		id: "pr",
		label: "PR Master",
	},
	{
		id: "prp1",
		label: "PR P1",
	},
	{
		id: "tr",
		label: "TR Master",
	},
	{
		id: "trb1",
		label: "TR B1",
	},
	{
		id: "trb3",
		label: "TR B3",
	},
];

const hasChosenReport = computed(() => {
	return values.version != null;
});

function parseMonthPickerDateToDate(date: MonthPickerDate) {
	if (!date) {
		return;
	}
	return new Date(date.year, date.month - 1, 1);
}

definePage({
	meta: {
		requiresAuth: true,
		customPermission: "hasReportsPageAccess",
	},
});
</script>
